<script setup>
import { ref, onMounted, onUnmounted, onBeforeUnmount } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';
import { storeToRefs } from 'pinia';
import { useToast } from 'primevue/usetoast';
import ApplicationMark from '@/components/general/ApplicationMark.vue';
import SettingsDropdown from '@/components/general/SettingsDropDown.vue';
import PrimaryButton from '@/components/general/PrimaryButton.vue';
import NotificationBell from '@/components/notifications/NotificationBell.vue';
import DarkModeToggle from '@/components/general/DarkModeToggle.vue';
import Sidebar from '@/components/layout/Sidebar.vue';
import { registerConnectivityListeners } from '@/pwa';
import { isPaymentReturn } from '@/utils/paymentReturnHandler';
import { registerPushNotificationServiceWorker, registerForPushNotifications, requestNotificationPermission } from '../pwa'

const authStore = useAuthStore();
const {user, isAuthenticated} = storeToRefs(authStore);

const showingNavigationDropdown = ref(false);
const sidebarOpen = ref(false);
const onlineStatus = ref(navigator.onLine);
const showPaymentReturnOverlay = ref(false);
const popupBlocked = ref(false);
const isCheckingPopup = ref(false);

const router = useRouter();
const route = useRoute();
const toast = useToast();

const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value;
};

const closeSidebar = () => {
  sidebarOpen.value = false;
};

// Handle online/offline status changes
const handleOnline = () => {
  onlineStatus.value = true;
  toast.add({
    severity: 'success',
    summary: 'You\'re back online',
    detail: 'All features are now available',
    life: 3000,
  });
};

const initPushNotifications = async () => {
  try {
    // Register push notification service worker
    await registerPushNotificationServiceWorker();

    // Check if notifications are already granted
    const permission = await requestNotificationPermission();

    // If permission is granted, register for push notifications
    if (permission === 'granted') {
      await registerForPushNotifications();
    }
  } catch (error) {
  }
};

const handleOffline = () => {
  onlineStatus.value = false;
  toast.add({
    severity: 'warn',
    summary: 'You\'re offline',
    detail: 'Some features may be limited',
    life: 5000,
  });
};

// Register connectivity listeners and get cleanup function
const cleanupConnectivityListeners = registerConnectivityListeners(handleOnline, handleOffline);


onMounted(() => {
    // Get user data with a timeout to prevent endless loading
    const userDataTimeout = setTimeout(() => {
      // If user data hasn't loaded after timeout, redirect to login
      if (!user.value) {
        router.replace({ name: ROUTES.HOME });
      }
    }, 5000); // 5 second timeout

    // Try to load user data
    authStore.getUser()
      .catch(() => {
        router.replace({ name: ROUTES.LOGIN });
      })
      .finally(() => {
        clearTimeout(userDataTimeout);
      });

    // Check if user is returning from payment
    checkPaymentReturn();
    initPushNotifications();

    // Check for popup blocking
    // checkPopupStatus();
})

onBeforeUnmount(() => {
    // Clean up connectivity listeners
    cleanupConnectivityListeners();
})

const goBack = () => {
    window.history.back();
};

// Check if user is returning from payment and show overlay
const checkPaymentReturn = () => {
    // Check if this is a payment return using URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const isReturningFromPayment = urlParams.has('payment_success') ||
                                  urlParams.has('payment_failed') ||
                                  urlParams.has('cache_bust') ||
                                  isPaymentReturn();

    // Check if we're in standalone PWA mode
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         window.navigator.standalone ||
                         document.referrer.includes('android-app://');

    // Only show overlay in PWA mode when returning from payment
    if (isReturningFromPayment && isStandalone) {
        showPaymentReturnOverlay.value = true;

        // Prevent scrolling while overlay is shown
        document.body.style.overflow = 'hidden';
    }
};

// Dismiss the payment return overlay
const dismissPaymentReturnOverlay = () => {
    showPaymentReturnOverlay.value = false;
    document.body.style.overflow = '';
};

const requestPermission = () => {
    if(!window.Notification) return;
    if (window.Notification.permission !== 'granted') {
        window.Notification.requestPermission().then(permission => {
            if (permission === 'granted') {
                initPushNotifications();
                window.location.reload();
            }
        });
    }
};

</script>

<template>
  <div v-if="user" class="flex overflow-hidden h-screen bg-gray-100 dark:bg-gray-900" @click="requestPermission">
    <!-- Popup Blocker Warning Banner -->
    <!-- <div v-if="popupBlocked" class="fixed top-0 right-0 left-0 z-50 p-4 text-center text-white bg-red-600">
      <div class="container flex justify-center items-center mx-auto">
        <svg class="mr-2 w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span class="mr-4">Popups are blocked. Please enable and refresh the page to get the best experience.</span>
      </div>
    </div> -->
    <!-- Payment Return Overlay - shown when returning from payment in PWA mode -->
    <div v-if="showPaymentReturnOverlay"
      class="flex fixed inset-0 z-50 flex-col justify-center items-center p-6 text-center text-white bg-black bg-opacity-80"
      @click="dismissPaymentReturnOverlay">
      <div class="mb-6 text-2xl font-bold">Welcome Back!</div>
      <div class="mb-8">Your payment has been processed</div>
      <div class="flex justify-center items-center mb-6 w-20 h-20 rounded-full border-4 border-white">
        <i class="text-4xl pi pi-check"></i>
      </div>
      <div class="mb-2 text-xl font-bold">Tap to Continue</div>
      <div class="text-sm text-gray-300">Tap anywhere on the screen to continue using the app</div>
    </div>
    <!-- Pattern background -->
    <div class="pattern-bg" aria-hidden="true"></div>
    <!-- Sidebar Component -->
    <Sidebar :isMobileOpen="sidebarOpen" @close="closeSidebar" />

    <!-- Main Content Area -->
    <div class="flex overflow-hidden flex-col flex-1 lg:ml-64">
      <!-- Offline notification banner -->
      <div v-if="!onlineStatus" class="visible offline-notification">
        <i class="mr-2 pi pi-wifi"></i> You're offline. Some features may be limited.
      </div>



      <nav class="bg-white border-b border-gray-100 dark:bg-gray-800 dark:border-gray-700">
        <!-- Primary Navigation Menu -->
        <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div class="flex justify-between h-16">
            <div class="flex space-x-4">
              <!-- Sidebar Toggle Button (Mobile) -->
              <button
                @click="toggleSidebar"
                class="inline-flex justify-center items-center p-2 text-gray-500 rounded-md transition duration-150 ease-in-out hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 focus:text-gray-700 lg:hidden">
                <svg class="w-6 h-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              <!-- Back Button -->

              <!-- Navigation Links -->
              <div
                class="hidden items-center space-x-8 text-xl font-semibold text-black dark:text-gray-200 sm:-my-px sm:ms-2 sm:flex">
                {{route.meta.title}}
              </div>
            </div>

            <div class="flex overflow-hidden items-center ms-6">
              <!-- Dark Mode Toggle -->
              <DarkModeToggle class="mr-4" />

              <!-- Notifications -->
              <NotificationBell class="mr-4" />

              <!-- Settings Dropdown -->
              <SettingsDropdown class="flex-1" />
            </div>
          </div>
        </div>
      </nav>
      <div class="flex relative items-center space-x-6 bg-gray-100 shrink-0 sm:hidden dark:bg-gray-800">
        <button
          @click="goBack"
          class="flex justify-center items-center p-2 text-gray-500 rounded-md transition duration-150 ease-in-out hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 focus:text-gray-700">
          <svg class="w-6 h-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span class="hidden ml-1 text-sm font-medium sm:inline">Back</span>
        </button>
        <div
          class="absolute left-1/2 flex-1 py-4 text-center text-gray-900 -translate-x-1/2 dark:text-gray-200">
          {{route.meta.title}}
        </div>
      </div>
      <!-- Page Content -->
      <main class="overflow-y-auto flex-1 px-4 pb-20 mt-2 sm:mt-0 sm:px-6 lg:px-8">
        <RouterView />
      </main>
    </div>
  </div>
</template>

<style scoped>
/* Spinner animation kept for other potential uses */
.spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(76, 175, 80, 0.3);
  border-radius: 50%;
  border-top-color: #4CAF50;
  animation: spin 1s ease-in-out infinite;
}

/* Offline notification */
.offline-notification {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  padding: 10px;
  background-color: #f44336;
  color: white;
  text-align: center;
  z-index: 35;
  font-size: 14px;
  transform: translateY(-100%);
  transition: transform 0.3s ease-out;
}

.offline-notification.visible {
  transform: translateY(0);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 640px) {
  /* Additional mobile-specific styles */
  main {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}

/* Add styles for the popup blocker banner */
.popup-blocker-banner {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(0);
  }
}
</style>

